import { Component, Inject } from '@angular/core';
import {
  DwActionEventInfo, DwActionPreference, DwButtonType, DwComponent, DwComponentType, DwDataSort, DwEventService,
  DwGridActionLocation, DwGridEvent, DwGridEventType, DwMetaDataActionExpressionService,
  DwMetaDataFilterTileState, DwMetaDataService, DwMetaDataServiceToken, DwQueryFilterGroup, DwRoutingService
} from '@devwareapps/devware-cap';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AppMetaData, AppMetaDataItemNames, QuestionTagEntity } from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';

@DwComponent({
  key: 'tags-grid-renderer', name: 'Tags Grid Renderer', isGlobal: false,
  parentItemName: AppMetaDataItemNames.QuestionTag, componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-tags-grid-renderer',
  templateUrl: './tags-grid-renderer.component.html',
  styleUrls: ['./tags-grid-renderer.component.scss']
})
export class TagsGridRendererComponent extends DwAviatorGridRendererBaseComponent<QuestionTagEntity> {
  search = '';
  hasMore = false;
  shownLabel = '';
  readonly skeletonRows = [1, 2, 3, 4, 5, 6];
  rows: { tag: QuestionTagEntity; actions: DwActionPreference[] }[] = [];
  leftActions: DwActionPreference[] = [];
  rightActions: DwActionPreference[] = [];

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    private actionExpressions: DwMetaDataActionExpressionService
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
    this.subscriptions.push(this.textFilterSubject.pipe(debounceTime(200), distinctUntilChanged()).subscribe(text => {
      this.currentSearchText = text.trim();
      this.customFiltersChanged();
    }));
  }

  setupRenderer(): void { this.rebuildActions(); }
  protected filterTileStateChange(state: DwMetaDataFilterTileState): void { }
  protected onGridConfigUpdated(): void { this.rebuildActions(); }
  protected getEffectiveSorts(): DwDataSort[] {
    return [{ fieldName: AppMetaData.QuestionTag.Attributes.TagName.fieldName, ascending: true } as DwDataSort];
  }

  customFiltersChanged(): void {
    this.searchTextAttrs = [AppMetaData.QuestionTag.Attributes.TagName];
    this.applyCustomFilters(new DwQueryFilterGroup());
  }

  clearSearch(): void { this.search = ''; this.applyTextFilter(''); }

  protected afterLoad(): void {
    this.hasMore = this.canGoToNextPage();
    this.shownLabel = `${this.items.length} tags${this.hasMore ? ' loaded' : ''}`;
    this.rebuildActions();
  }

  protected onLoadError(): void { this.rows = []; this.hasMore = false; this.shownLabel = ''; }

  onAction(event: DwActionEventInfo, tag: QuestionTagEntity = null): void {
    if (!event?.actionPreference) { return; }
    if (tag) { this.setSelectedItems([tag]); }
    this.onGridEvent({
      eventType: tag ? DwGridEventType.rowActionButton : DwGridEventType.gridActionButton,
      eventCommand: event.actionPreference.actionKey, actionPreference: event.actionPreference,
      eventData: tag, dwBrowserEvent: event.dwBrowserEvent
    } as DwGridEvent);
  }

  trackByTag(index: number, row: { tag: QuestionTagEntity }): number { return row.tag.QuestionTagId; }

  private rebuildActions(): void {
    const prefs = this.gridConfig?.actionPreferences || {};
    const context = (this.gridConfig as any)?.resolvedContext || null;
    const resolve = (location: string, row: QuestionTagEntity = null): DwActionPreference[] =>
      this.actionExpressions.executeActionEnabledVisibleRules(prefs[location] || [], row, context).initialResults || [];
    const keep = (action: DwActionPreference) => !/quick.?search|filter.?toggle/i.test(`${action.actionKey} ${action.actionDisplay}`);
    this.leftActions = resolve(DwGridActionLocation.actionBarLeft).filter(keep);
    this.rightActions = resolve(DwGridActionLocation.actionBarRight).filter(keep);
    this.rows = this.items.map(tag => ({ tag, actions: resolve(DwGridActionLocation.rowButton, tag).map(action => ({
      ...action, buttonConfig: { ...action.buttonConfig, buttonType: DwButtonType.standard,
        buttonClass: 'btn btn-secondary', buttonText: action.actionDisplay || action.buttonConfig?.buttonTitle,
        useParentDisplay: true }
    })) }));
  }
}
